import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useFieldOrBlockHidden } from 'components/common-form/hooks/useFieldOrBlockHidden';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import type { GridData, GridMetadata } from 'models/forms/fields';
import { FC, useCallback } from 'react';

import type { ProductNodeBuilderProps } from '../../ProductNodeBuilder.model';
import { GridCell } from './GridCell';

const emptyGridData: GridData = [];

export const DoubleValuationGridField: FC<ProductNodeBuilderProps> = ({
  node: { id },
  quoteWorkspaceVersion,
  batchWorkspaceVersion,
  mustForceAdvancedMode,
  tabIndex,
}) => {
  const { headers } = useFieldMetadata<GridMetadata>(id)!;
  const [gridData = emptyGridData, setGridData] = useData<GridData>(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);

  const isHidden = useFieldOrBlockHidden(
    id,
    undefined,
    quoteWorkspaceVersion,
    batchWorkspaceVersion,
    mustForceAdvancedMode,
  );

  const onCellValueChanged = useCallback(
    (row: number, cellId: string, value: unknown) => {
      const newRow: Record<string, unknown> = { ...gridData[row], [cellId]: value, isEdited: true };
      const newGridData: GridData = [...gridData.slice(0, row), newRow, ...gridData.slice(row + 1)];
      setGridData(newGridData);
    },
    [gridData, setGridData],
  );

  if (isHidden || gridData.length === 0) {
    return null;
  }
  const rowIndex = 0; // only one for now
  return (
    <>
      <div data-e2e={id} className={patchedFieldClassName}>
        <div key={rowIndex}>
          {headers.map((cellMetadata, columnIndex) => (
            <div key={`${cellMetadata.id}.${rowIndex + 1}`} className="p-1">
              <GridCell
                gridId={id}
                columnIndex={columnIndex}
                rowIndex={rowIndex}
                metaData={cellMetadata}
                value={gridData[rowIndex][cellMetadata.id] as string}
                onValueChanged={onCellValueChanged}
                tabIndex={tabIndex}
              />
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

DoubleValuationGridField.displayName = 'DoubleValuationGridField';
